<template>
  <div
    ref="modalElem"
    class="modal"
    @mousedown="backdropMouseDown"
    @mouseup="backdropMouseUp"
    :class="css"
  >
    <div class="window" @click.stop="click">
      <div class="header">
        <slot name="header"></slot>
        <button
          type="button"
          class="icon"
          :aria-label="$t('cancel')"
          :disabled="closeDisabled"
          @click="close"
        >
          <span class="material-symbols-outlined" aria-hidden="true">
            close
          </span>
        </button>
      </div>

      <div class="body">
        <slot name="body"></slot>
      </div>

      <div class="footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  ref,
  reactive,
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  Ref,
  defineComponent,
  watch,
} from "vue";

interface Props {
  active: boolean;
  scrolling?: boolean;
  closeDisabled?: boolean;
  shrinkToContent?: boolean;
}

interface CSSProps {
  active: boolean;
  scrolling: boolean;
  disable_close: boolean;
  shrink_to_content: boolean;
}

function resetOverflow() {
  document.documentElement.style.overflow = "auto";
}

function setOverflow() {
  document.documentElement.style.overflow = "hidden";
}

function resetOverflowWhenNoActiveModalRemains() {
  nextTick(() => {
    if (!document.querySelector(".modal.active")) {
      resetOverflow();
    }
  });
}

const returnFocusSetters = new WeakMap<
  HTMLElement,
  (target: HTMLElement) => void
>();

function focusModalControl(modal: HTMLElement) {
  const focusFirstMatching = (selector: string) => {
    for (const control of modal.querySelectorAll<HTMLElement>(selector)) {
      control.focus();
      if (document.activeElement === control) {
        return true;
      }
    }
    return false;
  };

  if (
    focusFirstMatching(
      '[autofocus], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]',
    )
  ) {
    return;
  }

  focusFirstMatching(
    'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
  );
}

export default defineComponent({
  props: {
    active: { required: true, type: Boolean },
    scrolling: { required: false, type: Boolean },
    closeDisabled: { required: false, type: Boolean },
    shrinkToContent: { required: false, type: Boolean },
  },
  setup(props: Props, context) {
    const modalElem = ref<HTMLElement>();
    let returnFocusElement: HTMLElement | null = null;
    let mountedModalElement: HTMLElement | null = null;

    function rememberFocusedElement() {
      const activeElement = document.activeElement;
      returnFocusElement =
        activeElement instanceof HTMLElement && activeElement !== document.body
          ? activeElement
          : null;
    }

    function restoreFocusedElement() {
      const target = returnFocusElement;
      if (!target) {
        return;
      }

      nextTick(() => {
        if (returnFocusElement !== target) {
          return;
        }

        const modal = modalElem.value;
        if (
          props.active &&
          modal?.isConnected &&
          modal.classList.contains("active")
        ) {
          return;
        }

        const activeModals =
          document.querySelectorAll<HTMLElement>(".modal.active");
        const activeModal = activeModals.item(activeModals.length - 1);
        if (
          activeModal &&
          activeModal !== modal &&
          !activeModal.contains(target)
        ) {
          returnFocusSetters.get(activeModal)?.(target);
          returnFocusElement = null;
          if (!activeModal.contains(document.activeElement)) {
            focusModalControl(activeModal);
          }
          return;
        }

        const activeElement = document.activeElement;
        if (
          activeElement instanceof HTMLElement &&
          activeElement !== document.body &&
          activeElement.isConnected &&
          (!modal || !modal.contains(activeElement))
        ) {
          returnFocusElement = null;
          return;
        }

        returnFocusElement = null;
        if (target.isConnected) {
          target.focus();
        }
      });
    }

    if (props.active) {
      rememberFocusedElement();
      setOverflow();
    }

    onMounted(() => {
      if (!modalElem.value) {
        return;
      }

      mountedModalElement = modalElem.value;
      returnFocusSetters.set(mountedModalElement, (target) => {
        returnFocusElement = target;
      });
    });

    onUnmounted(() => {
      resetOverflowWhenNoActiveModalRemains();
      window.removeEventListener("keydown", handleEscape);
      restoreFocusedElement();
      if (mountedModalElement) {
        returnFocusSetters.delete(mountedModalElement);
      }
    });

    watch(
      () => props.active,
      (active, prevActive) => {
        if (active) {
          rememberFocusedElement();
          setOverflow();
        } else {
          resetOverflowWhenNoActiveModalRemains();
          restoreFocusedElement();
        }
      },
    );

    const css = computed(() => {
      const cssClass: CSSProps = {
        active: props.active,
        scrolling: props.scrolling ? true : false,
        disable_close: props.closeDisabled ? true : false,
        shrink_to_content: props.shrinkToContent ? true : false,
      };

      return cssClass;
    });

    function close(event?: Event) {
      event?.stopPropagation();

      if (props.closeDisabled) {
        return;
      }

      context.emit("close");
      restoreFocusedElement();
    }

    function handleEscape(event: KeyboardEvent) {
      if (
        event.key !== "Escape" ||
        !props.active ||
        props.closeDisabled ||
        !modalElem.value
      ) {
        return;
      }

      const activeModals =
        document.querySelectorAll<HTMLElement>(".modal.active");
      if (activeModals.item(activeModals.length - 1) !== modalElem.value) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      close(event);
    }

    window.addEventListener("keydown", handleEscape);

    // A `click` fires on the common ancestor of mousedown and mouseup
    // targets, so a drag that starts inside the window (e.g. a cropper
    // resize handle) and ends on the backdrop would close the modal.
    // Close only when the press starts and ends on the backdrop itself.
    let pressStartedOnBackdrop = false;

    function backdropMouseDown(event: MouseEvent) {
      pressStartedOnBackdrop = event.target === event.currentTarget;
    }

    function backdropMouseUp(event: MouseEvent) {
      if (pressStartedOnBackdrop && event.target === event.currentTarget) {
        close(event);
      }
      pressStartedOnBackdrop = false;
    }

    function click() {
      document.body.dispatchEvent(new Event("close-ctrls"));
    }

    return {
      close,
      backdropMouseDown,
      backdropMouseUp,
      click,
      css,
      modalElem,
    };
  },
});
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: var(--classic-overlay, rgba(0, 0, 0, 0.8));
  display: none;
  z-index: 999;
}

.modal.active {
  display: block;
}

.window {
  position: relative;
  left: 50%;
  top: 50%;
  width: 470px;
  background: var(--classic-surface, #fff);
  box-shadow:
    1px 3px 3px 0 rgba(0, 0, 0, 0.2),
    1px 3px 15px 2px rgba(0, 0, 0, 0.2);
  border-radius: 3px;
  transform: translate(-50%, -50%);
  max-height: calc(80vh);
}

.modal.transparent {
  background-color: transparent;
}

.modal.hide_close .icon {
  display: none;
}

.modal.disable_close .icon {
  opacity: 0.2;
  cursor: initial;
  pointer-events: none;
}

.modal.medium > .window {
  width: 550px;
}

.modal.medium_large > .window {
  width: 750px;
}

.modal.large > .window {
  width: 70%;
  max-width: 950px;
  height: calc(80vh);
}

.modal.huge > .window {
  width: 90%;
  max-width: 1200px;
  height: calc(80vh);
}

.modal.content_scroll > .window {
  height: 80vh;
}

.modal.adaptive_footer > .window {
  display: flex;
  flex-direction: column;
}

.modal.adaptive_footer > .window > .body {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
}

.modal.adaptive_footer > .window > .footer {
  box-sizing: border-box;
  flex: 0 0 auto;
  height: auto;
  line-height: normal;
  min-height: 50px;
  padding-bottom: 6px;
  padding-top: 6px;
}

.header {
  font-weight: 600;
  font-size: 16px;
  padding: 0.8em 22px;
  border-bottom: 1px solid var(--classic-border, rgba(34, 36, 38, 0.15));
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.modal.no_header .header {
  display: none;
}

.header .icon {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  margin-left: auto;
  cursor: pointer;
}

.header .icon.left {
  float: left;
  margin-right: 5px;
}

.body {
  display: block;
  box-sizing: border-box;
  padding: 15px 22px;
  width: 100%;
  margin: 0;
  height: calc(100% - 100px);
  overflow: hidden;
}

.modal.scrolling .body {
  height: calc(80vh - 100px);
  overflow-y: auto;
  overflow-x: hidden;
}

.modal.scrolling.shrink_to_content:not(.large):not(.huge):not(.content_scroll)
  .body {
  height: auto;
  max-height: calc(80vh - 100px);
}

.footer {
  padding: 0 22px;
  line-height: 45px;
  height: 50px;
  border-top: 1px solid var(--classic-border, rgba(34, 36, 38, 0.15));
  background: var(--classic-surface-raised, #f9fafb);
  display: flex;
  align-items: center;
}

.modal.without_footer > .window > .footer {
  display: none;
}

.modal.without_footer > .window > .body {
  height: calc(100% - 50px);
}
</style>

<style>
.modal .footer .buttons {
  margin-left: auto;
  display: flex;
  align-items: center;
  width: 100%;
}

.modal .footer .buttons .right {
  margin-left: auto;
}

.modal .footer .button {
  margin-left: auto;
}

.modal .footer .buttons .right + .button,
.modal .footer .buttons .button + .button {
  margin-left: 1em;
}

.modal .footer .button.left {
  margin-left: 0;
}

.modal .body > div {
  width: 100%;
}

.modal .header .material-symbols-outlined {
  vertical-align: middle;
}
</style>
